import { PageSeo } from "@/components/PageSeo";
/**
 * Student Access — Email OTP (Fase 0/1 piloto)
 *
 * Flujo simplificado para estudiantes institucionales en cohortes con
 * access_mode='otp'. Dos pasos en una sola página:
 *   1) Email → signInWithOtp({ shouldCreateUser: false })
 *   2) Código OTP por email → verifyOtp({ type: 'email' })
 *
 * Coexiste con /auth/login (password). No afecta usuarios existentes.
 * La sesión devuelta por verifyOtp es la misma session+refresh-token que
 * el login con password, por lo que toda la infraestructura (useAuth,
 * AuthGate, useSessionManager, RLS) sigue funcionando sin cambios.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MassageBeachBackground } from "@/components/MassageBeachBackground";
import { trackAccessEvent } from "@/lib/access-telemetry";

const mbLearnLogo = "/mblearn-logo-HD.png";
const RESEND_COOLDOWN_SECS = 180;
const OTP_LENGTH = 8;

const isOtpRateLimitError = (message?: string) =>
  /email rate limit|rate limit exceeded|over_email_send_rate_limit|for security/i.test(
    message || "",
  );

const StudentAccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, session, loading: authLoading } = useAuth();

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(params.get("email")?.trim().toLowerCase() || "");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Redirect when already authenticated
  useEffect(() => {
    if (!authLoading && user && session) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, session, authLoading, navigate]);

  // Resend cooldown ticker
  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestCode = async (isResend = false) => {
    const normalized = (email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      toast.error("Ingresa un correo válido");
      return;
    }
    setSending(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: normalized,
        options: { shouldCreateUser: false },
      });

      if (error) {
        if (isOtpRateLimitError(error.message)) {
          trackAccessEvent("otp_failed", "/auth/student-access", {
            reason: isResend ? "request_rate_limited_resend" : "request_rate_limited",
          });
          setCooldown((current) => Math.max(current, RESEND_COOLDOWN_SECS));
          toast.error(
            isResend
              ? "Aún no podemos enviar otro código. Usa el último código recibido o espera unos minutos antes de reenviar."
              : "Llegaste al límite temporal de envíos. Espera unos minutos antes de solicitar otro código.",
          );
          return;
        }

        console.warn("[StudentAccess] signInWithOtp warning:", error.message);
        trackAccessEvent("otp_failed", "/auth/student-access", {
          reason: isResend ? "request_failed_resend" : "request_failed",
        });
        toast.error("No pudimos enviar el código. Intenta de nuevo en unos minutos.");
        return;
      }

      trackAccessEvent(
        isResend ? "otp_resent" : "otp_requested",
        "/auth/student-access",
        { email_domain: normalized.split("@")[1] || null },
      );
      setEmail(normalized);
      setRequestedAt(Date.now());
      setCooldown(RESEND_COOLDOWN_SECS);
      setStep("code");
      toast.success(
        isResend
          ? "Te enviamos un nuevo código"
          : "Si tu correo está registrado, recibirás un código en unos segundos",
      );
    } catch (err: any) {
      console.error("[StudentAccess] requestCode error:", err);
      trackAccessEvent("otp_failed", "/auth/student-access", {
        reason: isResend ? "request_exception_resend" : "request_exception",
      });
      toast.error("No pudimos enviar el código. Revisa tu conexión e inténtalo otra vez.");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (value: string) => {
    if (value.length !== OTP_LENGTH) return;
    setVerifying(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: value,
        type: "email",
      });
      if (error || !data.session) {
        const reason = /expired/i.test(error?.message || "")
          ? "expired"
          : /invalid|incorrect/i.test(error?.message || "")
            ? "invalid"
            : "unknown";
        trackAccessEvent("otp_failed", "/auth/student-access", { reason });
        toast.error(
          reason === "expired"
            ? "El código expiró. Solicita uno nuevo."
            : "Código incorrecto. Verifica e intenta de nuevo.",
        );
        setCode("");
        return;
      }
      const latency = requestedAt ? Date.now() - requestedAt : null;
      trackAccessEvent("otp_verified", "/auth/student-access", {
        latency_ms: latency,
      });
      toast.success("¡Acceso confirmado!");
      // useAuth listener will pick the session; navigate is just a fast path.
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      console.error("[StudentAccess] verifyCode error:", err);
      trackAccessEvent("otp_failed", "/auth/student-access", { reason: "exception" });
      toast.error("No pudimos verificar el código. Intenta de nuevo.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-x-hidden bg-background">
      <PageSeo
        path="/auth/student-access"
        title="Institutional student access — MBLearn"
        description="Access your MBLearn institutional account to continue your MBLEx training program."
        noindex
      />
      <MassageBeachBackground />

      <div className="mobile-container w-full max-w-[430px] p-4 sm:p-8 relative z-10 animate-fade-in">
        <div className="text-center mb-8 animate-scale-in">
          <img
            src={mbLearnLogo}
            alt="MBLearn"
            className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg"
          />
          <h1 className="text-3xl font-bold text-foreground">Acceso institucional</h1>
          <p className="text-muted-foreground mt-2">
            Ingresa tu correo y recibirás un código de acceso por email.
          </p>
        </div>

        <div className="bg-card/80 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-border animate-fade-in space-y-4">
          {step === "email" ? (
            <>
              <div>
                <Label htmlFor="otp-email" className="text-foreground">
                  Correo institucional
                </Label>
                <Input
                  id="otp-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.replace(/^\s+|\s+$/g, ""))}
                  className="bg-background text-foreground"
                />
              </div>

              <Button
                type="button"
                size="lg"
                className="w-full py-6 bg-gradient-to-r from-primary to-accent text-primary-foreground"
                disabled={sending || !email}
                onClick={() => requestCode(false)}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Enviar código
                  </>
                )}
              </Button>

              <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
                <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
                  Tu institución debe haberte inscrito previamente. Si tienes
                  contraseña, también puedes{" "}
                  <Link
                    to="/auth/login"
                    className="font-semibold underline"
                  >
                    ingresar con contraseña
                  </Link>
                  .
                </AlertDescription>
              </Alert>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                  }}
                >
                  <ArrowLeft className="h-3 w-3" />
                  Cambiar correo
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Ingresa el código recibido por email
                </p>
                <p className="font-semibold text-foreground">{email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Revisa tu bandeja y la carpeta de spam. Vence en 10 minutos.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Si ya pediste un código hace poco, usa el último correo recibido antes de reenviar.
                </p>
              </div>

              <div className="flex justify-center pt-2">
                <InputOTP
                  maxLength={OTP_LENGTH}
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    if (v.length === OTP_LENGTH) verifyCode(v);
                  }}
                  disabled={verifying}
                >
                  <InputOTPGroup>
                    {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                      <InputOTPSlot key={i} index={i} className="h-11 w-9 text-lg" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="button"
                size="lg"
                className="w-full"
                variant="default"
                disabled={verifying || code.length !== OTP_LENGTH}
                onClick={() => verifyCode(code)}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verificando…
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Entrar
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={cooldown > 0 || sending}
                  onClick={() => requestCode(true)}
                >
                  {cooldown > 0
                    ? `Reenviar código en ${cooldown}s`
                    : "Reenviar código"}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          ¿Problemas?{" "}
          <a
            href="mailto:support@mblearn.app"
            className="underline hover:text-foreground"
          >
            support@mblearn.app
          </a>
        </p>
      </div>
    </div>
  );
};

export default StudentAccess;
